import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import MenuClose from 'assets/icons/MenuClose.png'
import { RoundedButton } from 'common/Button/Button'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import { Formik } from 'formik'
import * as yup from 'yup'

import { TransformedFaberAndGlobalUsersType } from './UserProfileV3'

const nameRegex = (value: string) => {
  return /^[a-z '-]+$/i.test(value)
}
const validationSchema = yup.object({
  remarks: yup.string().required('Remarks are required'),
  firstName: yup
    .string()
    .required('First Name is required')
    .test({
      test(value, ctx) {
        if (value) {
          if (!nameRegex(value)) {
            return ctx.createError({
              message: 'name cannot contain special characters'
            })
          }
        }
        return true
      }
    }),
  lastName: yup
    .string()
    .required('Last Name is required')
    .test({
      test(value, ctx) {
        if (value) {
          if (!nameRegex(value)) {
            return ctx.createError({
              message: 'name cannot contain special characters'
            })
          }
        }
        return true
      }
    }),
  email: yup.string().required('Email is required').email('Invalid Email')
})

const headerMap = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email'
}
function UpdateUserDetailsDialog({
  faberToken,
  handleUserDialogClose,
  rowInfo,
  dialogOpen
}: UserDetailsProps) {
  const [initialValues, setInitialValues] = useState<UserDetails>({
    firstName: rowInfo.firstName,
    lastName: rowInfo.lastName,
    email: rowInfo.userEmail ? rowInfo.userEmail : '',
    remarks: ''
  })
  const {
    mutate: updateFaberUserDetails,
    isLoading: isUpdatingUserDetailsLoading
  } = useMutation(SubscriptionsAPI.updateFaberUserDetails, {
    onSuccess: () => {
      toast.success('Customer details updated')
    },
    onError: () => {
      toast.error('Error in updating customer details')
    },
    onSettled: () => {
      handleUserDialogClose()
    }
  })

  const onSubmit = (values) => {
    const allValues = {
      userId: values.userId,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      remarks: values.remarks
    }
    updateFaberUserDetails({ ...allValues, faberToken })
    setInitialValues(allValues)
  }

  return (
    <>
      <Dialog
        open={dialogOpen}
        maxWidth='md'
        fullWidth={true}
        onClose={handleUserDialogClose}
      >
        <DialogTitle
          sx={{
            margin: '0',
            fontWeight: '700',
            color: 'black',
            fontSize: '21px',
            mt: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box>Changes Made</Box>
          <IconButton
            onClick={handleUserDialogClose}
            size='small'
            data-cy='closeDialogBtn'
            sx={{
              mr: '-10px',
              mt: '-10px'
            }}
          >
            <img
              style={{
                width: '20px',
                height: '20px'
              }}
              src={MenuClose}
            />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ width: '100%' }} />
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({
            submitForm,
            handleChange,
            handleBlur,
            values,
            errors,
            touched,
            isValid
          }) => (
            <>
              <DialogContent sx={{ padding: '0' }}>
                <Grid
                  container
                  sx={{
                    mt: 2,
                    bgcolor: '#eceff4',
                    height: '5vh',
                    padding: '4px 0',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2
                  }}
                >
                  <Grid item xs={6}>
                    <TextColorFontBody
                      sx={{
                        fontWeight: 'bolder',
                        fontSize: '18px',
                        textAlign: 'left'
                      }}
                    >
                      Current
                    </TextColorFontBody>
                  </Grid>
                  <Grid item xs={6}>
                    <TextColorFontBody
                      sx={{
                        fontWeight: 'bolder',
                        fontSize: '18px',
                        textAlign: 'left'
                      }}
                    >
                      Changes made
                    </TextColorFontBody>
                  </Grid>
                </Grid>

                <Grid container px={2} mt={2} mb={1}>
                  {Object.keys(headerMap).map((fieldName, index) => {
                    return (
                      <React.Fragment key={fieldName}>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            fontWeight: 700,
                            textAlign: 'left',
                            mb: 1,
                            fontSize: '16px'
                          }}
                        >
                          {headerMap[fieldName]}
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            fontWeight: 700,
                            textAlign: 'left',
                            fontSize: '16px'
                          }}
                        >
                          {headerMap[fieldName]}
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{ textAlign: 'left', fontSize: '14px' }}
                        >
                          <TextColorFontBody>
                            {rowInfo[fieldName]}
                          </TextColorFontBody>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sx={{ textAlign: 'left', fontSize: '14px' }}
                        >
                          <TextField
                            value={values[fieldName]}
                            name={fieldName}
                            onChange={(e) => {
                              handleBlur(e)
                              handleChange(e)
                            }}
                            size='small'
                            sx={{ width: '80%' }}
                            error={
                              touched[fieldName] && Boolean(errors[fieldName])
                            }
                            helperText={touched[fieldName] && errors[fieldName]}
                          />
                        </Grid>

                        {index !== Object.keys(headerMap).length - 1 && (
                          <Divider sx={{ margin: '8px auto', width: '100%' }} />
                        )}
                      </React.Fragment>
                    )
                  })}
                  <Grid item xs={6} mt={1}>
                    <TextColorFontBody
                      sx={{ fontSize: '16px', fontWeight: 700 }}
                    >
                      Remarks
                    </TextColorFontBody>
                    <TextField
                      variant='standard'
                      id='remarks'
                      multiline
                      fullWidth
                      value={values.remarks}
                      onChange={(e) => {
                        handleBlur(e)
                        handleChange(e)
                      }}
                      error={touched.remarks && Boolean(errors.remarks)}
                      helperText={touched.remarks && errors.remarks}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <RoundedButton variant='outlined'>Cancel</RoundedButton>
                <LoadingButton
                  type='submit'
                  variant='filled'
                  loading={isUpdatingUserDetailsLoading}
                  loadingIndicator={
                    <CircularProgress color='inherit' size={16} />
                  }
                  disabled={!isValid}
                  onClick={() => submitForm()}
                >
                  Confirm
                </LoadingButton>
              </DialogActions>
            </>
          )}
        </Formik>
      </Dialog>
    </>
  )
}

export default UpdateUserDetailsDialog

interface UserDetailsProps {
  faberToken: string
  handleUserDialogClose: () => void
  dialogOpen: boolean
  rowInfo: TransformedFaberAndGlobalUsersType
}

export interface UserDetails {
  firstName: string
  lastName: string
  email: string
  remarks: string
}
